import { Parameter } from './parameter.model';
import { TuningController, TuningControllerType } from './tuning-controller.model';

export function tuningControllerParameterId(
  tuningControllerParameter: Partial<TuningControllerParameter>,
  tuningController: Partial<TuningController>,
) {
  return `${tuningControllerParameter.fieldName}.${tuningController.id}`;
}

/**
 * Represents a single tuning controller parameter like damping ratio or bandwidth.
 */
export interface TuningControllerParameter extends Parameter {
  /**
   * Belongs to TuningController [FK].
   */
  tuningControllerId: string;

  /**
   * Optionally belongs to SignalGeneratorResult [FK].
   */
  signalGeneratorResultId?: string;
}

export type TuningControllerParametersMap = Map<TuningControllerType, Partial<TuningControllerParameter>[]>;

export const tuningControllerParametersMap: TuningControllerParametersMap = new Map();

tuningControllerParametersMap.set('position', [
  {
    defaultData: 1,
    description:
      '<p>Cascaded controller configuration allows to change structure of the cascade controller.</p><p><strong>PI-P</strong> structure generally has faster response, overshoot but good tracking: zero steady state error for constant and ramp inputs.</p><p><strong>P-PI</strong> structure generally has damped response but worse tracking: steady state error is non-zero for ramp input.</p>',
    fieldName: 'controllerType',
    floatValue: 0,
    group: 'Cascaded PID Tuning',
    inputType: 'select',
    intValue: 1,
    mandatory: true,
    max: 2,
    min: 1,
    name: 'Cascaded controller configuration',
    options: {
      'PI-P': 1,
      'P-PI': 2,
    },
    typeValue: 'intValue',
    unit: '',
    value: 1,
    writeAccess: true,
  },
  {
    defaultData: 1,
    description:
      '<p>Damping ratio is a parameter of an oscillatory system that reduces oscillations.</p><p>  In case of the cascade control loop, this parameter indirectly influences overshoot and rise time.  Practically, the damping ratio effect depends on cascaded controller configuration.  Generally, values > 1 can be used for removing overshoot but at cost of slower settling time, values < 1 can be used to achieve sharper response at cost of overshoot.</p>',
    fieldName: 'positionDamping',
    floatValue: 1,
    group: 'Cascaded PID Tuning',
    inputType: 'range',
    intValue: 0,
    mandatory: true,
    max: 2,
    min: 0.4,
    name: 'Damping Ratio',
    rangeStep: 0.1,
    typeValue: 'floatValue',
    unit: '',
    value: 1,
    writeAccess: true,
    digitsInfo: '1.2-2',
  },
  {
    defaultData: 200,
    description:
      '<p>Settling time is a time in ms needed to settle the step response within 5% of the reference value.</p><p>Settling time limit is a warning that appears when computed controller gains may result in triggering overcurrent during ⅙ motor revolution step response.</p>',
    fieldName: 'settlingTime',
    floatValue: 0,
    group: 'Cascaded PID Tuning',
    inputType: 'range',
    intValue: 200,
    mandatory: true,
    max: 500,
    min: 20,
    name: 'Settling Time',
    typeValue: 'intValue',
    unit: 'ms',
    value: 200,
    writeAccess: true,
  },
]);

tuningControllerParametersMap.set('velocity', [
  {
    defaultData: 0.7,
    description:
      '<p>Damping ratio is a parameter of an oscillatory system that reduces oscillations.</p><p>In case of the velocity control loop, this parameter governs overshoot and rise time. Practically, the damping ratio can be divided into 2 regions:<ul>  <li><strong>underdamped &lt; 1</strong> - overshoot is allowed, sharper response</li>  <li><strong>overdamped &gt; 1</strong> - no overshoot, less sharp response</li></ul>Settling time in both case is ideally the same.</p>',
    fieldName: 'velocityDamping',
    floatValue: 0.7,
    group: 'Velocity Tuning',
    inputType: 'range',
    intValue: 0,
    mandatory: true,
    max: 2,
    min: 0.4,
    name: 'Damping Ratio',
    rangeStep: 0.1,
    typeValue: 'floatValue',
    unit: '',
    value: 0.7,
    writeAccess: true,
    digitsInfo: '1.2-2',
  },
  {
    defaultData: 5,
    description:
      '<p>Bandwidth or so-called -3 dB bandwidth is a characteristic of a control loop that characterise sharpness of the control loop.</p><p>Basically, it is a frequency the system may follow with 71% of amplitude.</p>',
    fieldName: 'velocityLoopBandwidth',
    floatValue: 5,
    group: 'Velocity Tuning',
    inputType: 'range',
    intValue: 0,
    mandatory: true,
    max: 100,
    min: 0.2,
    name: 'Bandwidth',
    rangeStep: 0.1,
    typeValue: 'floatValue',
    unit: 'Hz',
    value: 5,
    writeAccess: true,
    digitsInfo: '1.2-2',
  },
]);

tuningControllerParametersMap.set('torque', []);
